import { observer } from 'mobx-react-lite'
import React, { memo, useContext } from 'react'
import classNames from 'classnames'

// evertel
import { NormalizedMedia } from '@evertel/media'
import { APIDataDepartmentUserThrough } from '@evertel/types'
import { DisplayRoomMessage, DisplayThreadMessage } from '@evertel/stores'
import { Col, Icon, PhotoGalleryContext, Row } from '@evertel/web/ui'
import { Media } from '@evertel/web/ui'
import { scaleMedia, sortMediaByType } from '@evertel/utils'
import { useWindowDimensions } from '@evertel/web/hooks'
import { parsedContent } from '../../../utils/message'
import { MessageBodyAction } from './MessageBodyAction'
import { OnboardingStepsAction } from './OnboardingStepsAction'

export interface MessageBodyProps {
    message: DisplayRoomMessage | DisplayThreadMessage
    media?: NormalizedMedia[]
    highlightString?: string
    userDepartmentsAccess?: APIDataDepartmentUserThrough[]
    variant?: 'list-item' | 'search' | 'reply'
}

const MessageBody: React.FC<MessageBodyProps> = observer(({
    message,
    media,
    highlightString
}) => {
    const photoGallery = useContext(PhotoGalleryContext)

    const { width: windowWidth, height: windowHeight } = useWindowDimensions()
    const maxMediaWidth = windowWidth * 0.5
    const maxMediaHeight = windowHeight * 0.3

    const onClickMedia = (mediaId: number) => {
        //Prevent error if PhotoGallery context is not yet available
        if (!photoGallery?.setMedia) return

        photoGallery.setMedia(media, mediaId)
        photoGallery.setVisible(true)
    }

    // if (!message?.text && !media?.length) return null

    const mediaSorted = sortMediaByType(media)

    return (
        <Col>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {message.isUrgent && (
                    <span className="urgent-icon">
                        <Icon
                            name="triangle-exclamation"
                        />
                    </span>
                )}

                <div
                    dangerouslySetInnerHTML={{ __html: parsedContent(message.text, highlightString) }}
                    className={classNames('post-content', {'urgent': message.isUrgent} )}
                    style={{ display: 'inline-block' }}
                />
            </div>
            {(!!mediaSorted?.length) && (
                <Row className="d-flex flex-wrap">
                    {mediaSorted.map((m) => {
                        const scaledDimensions =
                            ['image', 'video'].includes(m.mediaType)
                                ? scaleMedia({
                                    maxWidth: maxMediaWidth,
                                    maxHeight: maxMediaHeight,
                                    naturalHeight: m.height,
                                    naturalWidth: m.width
                                })
                                : undefined
                        return (
                            <React.Fragment key={m.id}>
                                {React.createElement(
                                    m.mediaType === 'image' ? 'button' : 'div',
                                    {
                                        className: classNames('mt-2 me-2', {
                                            'media-clickable': ['image', 'video'].includes(m.mediaType)
                                        }),
                                        onClick: m.mediaType === 'image' ? () => onClickMedia(m.id) : undefined
                                    },
                                    <Media
                                        media={m}
                                        width={scaledDimensions?.width}
                                        height={scaledDimensions?.height}
                                        rounded
                                        enableHover={true}
                                        enableContextMenu={true}
                                        fileProps={{
                                            width: undefined,
                                            maxWidth: maxMediaWidth
                                        }}
                                        videoProps={{
                                            maxWidth: '100%',
                                            maxHeight: 'calc(50vh)',
                                            minWidth: '200px',
                                            ...(message.meta.isForwarded ? { fallbackWidth: '100%' } : {})
                                        }}
                                        imageProps={{
                                            // TODO: make work with fix-fluid for dynamic image resizing in rooms
                                            flexibility: 'fixed'
                                        }}
                                    />
                                )}
                            </React.Fragment>
                        )
                    })}
                </Row>
            )}

            {(!!message.meta?.cta?.length) &&
                <MessageBodyAction message={message} />
            }

            {!!message.meta?.welcomeMessage && (
                <OnboardingStepsAction/>
            )}

        </Col>
    )
})

MessageBody.displayName = 'MessageBody'

export { MessageBody }
